import { Alert, Card } from 'antd';
import { styled } from 'styled-components';

export const InsightsPanel = styled(Card)`
  container-type: inline-size;
  container-name: inspection-insights;
  min-width: 0;
  max-width: 100%;

  .ant-card-body {
    min-width: 0;
  }

  @media (max-width: 767px) {
    .ant-card-body {
      padding: 12px;
    }
  }
`;

export const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 16px;
  flex-wrap: wrap;
  margin-bottom: 12px;
  min-width: 0;

  @container inspection-insights (max-width: 640px) {
    flex-direction: column;
    align-items: stretch;
  }
`;

export const Heading = styled.div`
  min-width: 0;
  flex: 1 1 240px;

  @container inspection-insights (max-width: 640px) {
    flex: 0 1 auto;
  }
`;

export const Controls = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: stretch;
  gap: 8px;
  max-width: 100%;

  .ant-segmented.ant-segmented-block {
    width: max-content;
    max-width: 100%;
  }

  .ant-segmented.ant-segmented-block .ant-segmented-item {
    flex: 1 0 auto;
    width: auto;
    min-width: max-content;
  }

  .ant-segmented-item-label {
    display: flex;
    align-items: center;
    justify-content: center;
    text-align: center;
    white-space: nowrap;
  }

  .ant-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
  }

  @container inspection-insights (max-width: 640px) {
    flex-direction: column;

    .ant-segmented,
    .ant-btn {
      width: 100%;
    }

    .ant-segmented.ant-segmented-block .ant-segmented-item {
      flex: 1 1 0;
      min-width: 0;
    }

    .ant-segmented-item-label {
      width: 100%;
      white-space: normal;
    }
  }
`;

export const SegmentLabel = styled.span`
  display: grid;
  justify-items: center;
  align-items: center;

  > span {
    grid-area: 1 / 1;
    white-space: nowrap;
  }
`;

export const SegmentSizer = styled.span`
  visibility: hidden;
  pointer-events: none;

  @container inspection-insights (max-width: 640px) {
    display: none;
  }
`;

export const Title = styled.h3`
  margin: 0;
  overflow-wrap: anywhere;
`;

export const Meta = styled.div`
  color: #667085;
  font-size: 12px;
  margin-top: 4px;
  overflow-wrap: anywhere;
`;

export const LoadingState = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 120px;
`;

export const CardGrid = styled.div<{ $expanded: boolean }>`
  display: grid;
  gap: 8px;
  grid-template-columns: ${({ $expanded }) =>
    $expanded ? 'repeat(6, minmax(0, 1fr))' : 'repeat(7, minmax(0, 1fr))'};

  @container inspection-insights (max-width: 960px) {
    grid-template-columns: ${({ $expanded }) =>
      $expanded ? 'repeat(3, minmax(0, 1fr))' : 'repeat(4, minmax(0, 1fr))'};
  }

  @container inspection-insights (max-width: 640px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  @container inspection-insights (max-width: 420px) {
    grid-template-columns: minmax(0, 1fr);
  }
`;

export const CardLabel = styled.div`
  color: #667085;
  font-size: 12px;
  min-width: 0;
`;

export const CardCount = styled.div<{ $expanded: boolean }>`
  font-size: ${({ $expanded }) => ($expanded ? '28px' : '20px')};
  font-weight: 700;
  flex-shrink: 0;
`;

export const InsightCard = styled.button<{
  $selected: boolean;
  $expanded: boolean;
}>`
  display: flex;
  flex-direction: ${({ $expanded }) => ($expanded ? 'column' : 'row')};
  align-items: ${({ $expanded }) => ($expanded ? 'stretch' : 'center')};
  justify-content: ${({ $expanded }) =>
    $expanded ? 'flex-start' : 'space-between'};
  gap: ${({ $expanded }) => ($expanded ? '0' : '8px')};
  min-width: 0;
  max-width: 100%;
  text-align: left;
  border: ${({ $selected }) =>
    $selected ? '2px solid #2f6fed' : '1px solid #eef2f6'};
  border-radius: 10px;
  padding: 12px;
  background: #fff;
  cursor: pointer;

  ${CardLabel} {
    overflow: ${({ $expanded }) => ($expanded ? 'visible' : 'hidden')};
    text-overflow: ${({ $expanded }) => ($expanded ? 'clip' : 'ellipsis')};
    white-space: ${({ $expanded }) => ($expanded ? 'normal' : 'nowrap')};
  }
`;

export const CardDetail = styled.div<{ $tone?: 'up' | 'down' }>`
  font-size: 12px;
  color: ${({ $tone }) =>
    $tone === 'up' ? '#dc2626' : $tone === 'down' ? '#16a34a' : '#667085'};
  min-width: 0;
  overflow-wrap: anywhere;
`;

export const CardNote = styled.div<{ $alert?: boolean }>`
  font-size: 12px;
  color: ${({ $alert }) => ($alert ? '#dc2626' : 'inherit')};
  min-width: 0;
  overflow-wrap: anywhere;
`;

export const SummaryTile = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  min-width: 0;
  max-width: 100%;
  border: 1px solid #eef2f6;
  border-radius: 10px;
  padding: 12px;

  ${CardLabel} {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`;

export const SummaryValue = styled.div`
  font-size: 20px;
  font-weight: 700;
  flex-shrink: 0;
`;

export const Charts = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  gap: 16px;
  margin-top: 16px;

  @container inspection-insights (max-width: 900px) {
    grid-template-columns: minmax(0, 1fr);
  }
`;

export const ChartBlock = styled.div`
  min-width: 0;

  > strong {
    display: block;
    overflow-wrap: anywhere;
  }
`;

export const ChartHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  margin-bottom: 8px;

  strong {
    min-width: 0;
    overflow-wrap: anywhere;
  }
`;

export const ChartFrame = styled.div<{ $spaced?: boolean }>`
  width: 100%;
  height: 280px;
  min-width: 0;
  overflow: hidden;
  margin-top: ${({ $spaced }) => ($spaced ? '8px' : '0')};

  @container inspection-insights (max-width: 640px) {
    height: 240px;
  }
`;

export const DrillRow = styled.div`
  margin-top: 12px;
`;

export const Hint = styled.span`
  color: #667085;
  font-size: 12px;
`;

export const IntersectionAlert = styled(Alert)`
  margin-top: 12px;
`;
